/**
 * Registro de la cara de un usuario (Operaciones → Usuarios).
 *
 * La cara se captura con la cámara del equipo y se guarda **sólo el descriptor** (un vector de
 * números): el servidor no recibe ni almacena la foto. Se admiten varias capturas del mismo rostro
 * para que el reconocimiento funcione con gafas, con gorra o con otra luz.
 *
 * La biometría es un dato personal sensible (Ley 29733): se debe registrar con el consentimiento
 * del usuario y borrarla cuando la persona deja la empresa (botón «Borrar la cara»).
 */

import { ScanFace, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CamaraFacialDialog } from '@/features/auth/components/camara-facial-dialog';
import { biometriaApi } from '@/features/auth/biometria.api';
import { MODELO_ROSTRO } from '@/lib/biometria/rostro';
import { ApiError } from '@/lib/http/api-error';
import { formatDateTimeValue } from '@/lib/utils';

interface BiometriaUsuarioDialogoProps {
  usuarioId: number;
  usuarioNombre: string;
  /** ¿Puede registrar la cara de esta persona? (permiso de edición de usuarios) */
  puedeEditar: boolean;
  onCerrar: () => void;
  onAviso: (texto: string) => void;
}

interface EstadoBiometria {
  cargando: boolean;
  registrada: boolean;
  muestras: number;
  ultima: string | null;
}

/** Cuántas capturas se piden al registrar la cara. */
const CAPTURAS = 5;

function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function BiometriaUsuarioDialogo({
  usuarioId,
  usuarioNombre,
  puedeEditar,
  onCerrar,
  onAviso,
}: BiometriaUsuarioDialogoProps) {
  const [estado, setEstado] = useState<EstadoBiometria>({
    cargando: true,
    registrada: false,
    muestras: 0,
    ultima: null,
  });

  const [camaraAbierta, setCamaraAbierta] = useState(false);
  const [intento, setIntento] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [porBorrar, setPorBorrar] = useState(false);

  useEffect(() => {
    let cancelado = false;

    void (async () => {
      try {
        const datos = await biometriaApi.estado(usuarioId);

        if (cancelado) return;

        setEstado({
          cargando: false,
          registrada: datos.registrada,
          muestras: datos.muestras,
          ultima: datos.ultima,
        });
      } catch (fallo) {
        if (cancelado) return;

        setEstado((actual) => ({ ...actual, cargando: false }));
        setError(mensajeDe(fallo, 'No se pudo leer la biometría del usuario.'));
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [usuarioId]);

  const registrar = async (descriptores: number[][], calidad: number) => {
    setError(null);
    setGuardando(true);

    try {
      const datos = await biometriaApi.registrar({
        descriptores,
        modelo: MODELO_ROSTRO,
        usuario_id: usuarioId,
        etiqueta: 'Registro desde Usuarios',
        calidad,
      });

      setCamaraAbierta(false);
      setEstado({
        cargando: false,
        registrada: datos.registrada,
        muestras: datos.muestras,
        ultima: datos.ultima,
      });
      onAviso(`${usuarioNombre} ya puede entrar con su cara (${datos.muestras} capturas).`);
    } catch (fallo) {
      setError(mensajeDe(fallo, 'No se pudo registrar la cara.'));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    setError(null);
    setGuardando(true);

    try {
      const datos = await biometriaApi.borrarDeUsuario(usuarioId);
      setPorBorrar(false);
      setEstado({
        cargando: false,
        registrada: datos.registrada,
        muestras: datos.muestras,
        ultima: datos.ultima,
      });
      onAviso(`Se dio de baja la biometría de ${usuarioNombre}.`);
    } catch (fallo) {
      setError(mensajeDe(fallo, 'No se pudo borrar la cara.'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <Dialog
        open
        onClose={onCerrar}
        size="sm"
        title={`Cara de ${usuarioNombre}`}
        description="Para entrar sin escribir la contraseña."
        footer={
          <>
            <Button variant="ghost" onClick={onCerrar}>
              Cerrar
            </Button>

            {estado.registrada ? (
              <Button
                variant="danger"
                leftIcon={<Trash2 className="size-4" />}
                disabled={!puedeEditar || guardando}
                onClick={() => setPorBorrar(true)}
              >
                Borrar la cara
              </Button>
            ) : null}

            <Button
              leftIcon={<ScanFace className="size-4" />}
              disabled={!puedeEditar || guardando}
              loading={guardando}
              onClick={() => {
                setError(null);
                setCamaraAbierta(true);
              }}
            >
              {estado.registrada ? 'Registrar otra vez' : 'Registrar cara'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {error ? <Alert variant="error">{error}</Alert> : null}

          {!puedeEditar ? (
            <Alert variant="warning" title="Sin permiso">
              Necesitas permiso para editar usuarios y así registrar la cara.
            </Alert>
          ) : null}

          {estado.cargando ? (
            <Skeleton className="h-20 w-full" />
          ) : estado.registrada ? (
            <Alert variant="success" title="Reconocimiento activo">
              {usuarioNombre} puede entrar mirando a la cámara.
              <span className="mt-1 block text-xs">
                {estado.muestras} {estado.muestras === 1 ? 'captura' : 'capturas'}
                {estado.ultima ? ` · última el ${formatDateTimeValue(estado.ultima)}` : ''}
              </span>
            </Alert>
          ) : (
            <Alert variant="info" title="Sin cara registrada">
              Todavía no se registró el rostro de {usuarioNombre}. Hasta entonces debe entrar con su
              contraseña o su código.
            </Alert>
          )}

          <div className="space-y-2 text-xs leading-relaxed text-ink-500">
            <p>
              Se piden <strong>{CAPTURAS} capturas</strong> mirando de frente; conviene variar un
              poco la posición entre una y otra para que quede registrada con y sin lentes.
            </p>
            <p>
              Sólo se guarda un código numérico que representa la cara: <strong>no se guardan
              fotos</strong>. Pide el consentimiento del trabajador antes de registrarla y bórrala
              cuando deje la empresa.
            </p>
          </div>
        </div>
      </Dialog>

      {camaraAbierta ? (
        <CamaraFacialDialog
          key={intento}
          open
          onClose={() => setCamaraAbierta(false)}
          titulo={`Registrar la cara de ${usuarioNombre}`}
          descripcion={`Se pedirán ${CAPTURAS} capturas: mira de frente y espera a que se capturen solas.`}
          capturas={CAPTURAS}
          onCapturas={registrar}
          ocupado={guardando}
          error={error}
          onReintentar={() => {
            setError(null);
            setIntento((actual) => actual + 1);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={porBorrar}
        title="¿Borrar la cara registrada?"
        description={`${usuarioNombre} ya no podrá entrar con la cámara; seguirá entrando con su contraseña o su código.`}
        confirmLabel="Sí, borrar"
        loading={guardando}
        onClose={() => setPorBorrar(false)}
        onConfirm={borrar}
      />
    </>
  );
}
